import { NoteResponse } from '~/lib/types/notes/NoteResponse';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import {
  coalesceNoteContentToHtml,
  FireflyTertiaryButtonMenuItem,
} from '@equip.health/ui';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { isEmpty } from 'lodash';
import { capitalizeFirstLetter } from '~/lib/common';
import { getParsedNoteActivityDate } from '~/lib/util/patient.util';
import { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { fillActivityWith } from '~/components/maud/patients/note/editNoteForm.util';
import { useGetAdminNoteActivityWithNamesEnumQuery } from '~/store/services/enumerations';

type Props = {
  note?: NoteResponse;
  appointmentDetails: AppointmentDetailResponse | null;
  setUploadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

export const useInitialEditNoteFormValues = ({
  note,
  appointmentDetails,
  setUploadedFiles,
}: Props) => {
  const { noteTemplateRevisions } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      note?.noteTemplateRevisionId,
    );

  const [initialValues, setInitialValues] =
    useState<EditNoteFormData>(undefined);

  const { data: activityWithNames } =
    useGetAdminNoteActivityWithNamesEnumQuery();

  useEffect(() => {
    if (note) {
      const {
        appointmentExternalId,
        addendumContent,
        attachments,
        isConfidential,
        noteCommunicationMethod,
        noteContent,
        noteTemplateRevisionId,
        noteTemplateRevisionData,
        noteType,
        sessionSummary,
        title,
        aiSummaryId,
      } = note;

      let revisionOption: FireflyTertiaryButtonMenuItem =
        NO_NOTE_TEMPLATE_OPTION;
      if (noteTemplateRevisions && noteTemplateRevisionId !== null) {
        const selectedNoteTemplateLabel = noteTemplateRevisions.find(
          (rev) => rev.templateRevisionId === noteTemplateRevisionId,
        )?.template.templateTitle;

        revisionOption = {
          node: noteTemplateRevisionId,
          label: selectedNoteTemplateLabel,
        };
      }

      const titleOrApptLabelByDefault = isEmpty(title)
        ? appointmentDetails?.appointmentTypeLabel
        : title || '';

      let activity = (note.activityWith ?? []).map((activityWithItem) => ({
        code: activityWithItem?.code,
        label: capitalizeFirstLetter(activityWithItem.activityWithName),
        value: capitalizeFirstLetter(activityWithItem.activityWithName),
      }));

      if (appointmentDetails && activity.length === 0 && activityWithNames) {
        activity = fillActivityWith(
          appointmentDetails,
          activityWithNames.map((activityWithName) => ({
            code: activityWithName?.code,
            label: activityWithName.activityWithName,
            value: activityWithName.activityWithName,
          })),
        ) as { code: string; label: string; value: string }[];
      }

      setInitialValues({
        aiSummaryId,
        activity,
        activityDate: getParsedNoteActivityDate(note.activityDate),
        addendumContent: coalesceNoteContentToHtml(addendumContent ?? ''),
        appointment: appointmentExternalId
          ? { label: '', node: appointmentExternalId }
          : null,
        isConfidential,
        medium: noteCommunicationMethod
          ? {
              label: capitalizeFirstLetter(noteCommunicationMethod),
              value: capitalizeFirstLetter(noteCommunicationMethod),
            }
          : null,
        noteTemplateRevision: revisionOption,
        noteTemplateRevisionData,
        noteContentCache: coalesceNoteContentToHtml(noteContent),
        sessionSummary: sessionSummary || null,
        title: titleOrApptLabelByDefault,
        type: noteType
          ? {
              label: capitalizeFirstLetter(noteType),
              value: capitalizeFirstLetter(noteType),
            }
          : null,
      });
      setUploadedFiles(attachments);
    }
  }, [note, noteTemplateRevisions, appointmentDetails, activityWithNames]);

  return { initialValues };
};
